import { gql } from '@apollo/client';

// GraphQL Schema Definitions
export enum ReportableType {
  CHANNEL = 'CHANNEL',
  VIDEO = 'VIDEO',
  REEL = 'REEL',
}

export type ReportCaseStatus = 'OPEN' | 'IN_REVIEW' | 'RESOLVED';

export const GET_REPORT_REASONS = gql`
  query GetReportReasons($context: ReportReasonContext) {
    getReportReasons(context: $context) {
      rrId
      rrCode
      rrLabel
    }
  }
`;

export interface ReportReason {
  rrId: string;
  rrCode: string;
  rrLabel: string;
}

export interface GetReportReasonsData {
  getReportReasons: ReportReason[];
}

export interface GetReportReasonsVariables {
  context?: ReportReasonContext;
}

export type ReportReasonContext = 'CHANNEL' | 'CONTENT' | 'USER' | string;

// REPORT CHANNEL MUTATION & TYPES
export const REPORT_CHANNEL = gql`
  mutation ReportChannel($input: ReportChannelInput!) {
    reportChannel(input: $input) {
      message
      reportCaseId
    }
  }
`;

export interface ReportChannelInput {
  chId: string;
  reasonCode: string;
  details?: string;
}

export interface ReportChannelData {
  reportChannel: {
    message: string;
    reportCaseId: string;
  };
}

export interface ReportChannelVariables {
  input: ReportChannelInput;
}

// Query for getting reports queue counts
export const GET_REPORTS_QUEUE_COUNTS = gql`
  query GetReportsQueueCounts {
    getReportsQueueCounts {
      open
      inReview
      resolved
    }
  }
`;

export interface GetReportsQueueCountsData {
  getReportsQueueCounts: {
    open: number;
    inReview: number;
    resolved: number;
  };
}

export interface TakenByUser {
  uId: string;
  uEmailId: string;
  uFullName: string;
  uAvatarUrl?: string;
}

export interface ModerationActionDetails {
  modaId: string;
  rcId: string;
  actionType: string;
  actionLabel: string;
  message: string;
  warningTypes?: WarningTypeDetail[];
  durationDays?: number | null;
  suspensionPeriodLabel?: string;
  effectiveUntil?: string;
  takenBy?: TakenByUser | null;
  takenOn?: string;
}

export type GetReportsQueueCountsVariables = Record<string, never>;

export const GET_REPORTS_QUEUE = gql`
  query GetReportsQueue($input: GetReportsQueueInput) {
    getReportsQueue(input: $input) {
      items {
        rcId
        reportableType
        reportableId
        status
        title
        thumbnailUrl
        reportsCount
        lastReportedAt
      action {
        modaId
        rcId
        actionType
        actionLabel
        message
        warningTypes {
          rrId
          rrCode
          rrLabel
        }
        durationDays
        suspensionPeriodLabel
        effectiveUntil
        takenBy {
          uId
          uEmailId
          uFullName
          uAvatarUrl
        }
        takenOn
      }
      }
      totalCount
    }
  }
`;

export interface ReportsQueueItem {
  rcId: string;
  reportableType: ReportableType;
  reportableId: string;
  status: string;
  title: string;
  thumbnailUrl: string;
  reportsCount: number;
  lastReportedAt: string;
  action?: ModerationActionDetails | null;
}

export interface GetReportsQueueData {
  getReportsQueue: {
    items: ReportsQueueItem[];
    totalCount: number;
  };
}

export interface GetReportsQueueInput {
  status: ReportCaseStatus;
  offset: number;
  limit: number;
  startDate?: string;
  endDate?: string;
  reportableTypes?: ReportableType[];
}

export interface GetReportsQueueVariables {
  input: GetReportsQueueInput;
}


export const REPORT_MEDIA = gql`
  mutation ReportMedia($input: ReportMediaInput!) {
    reportMedia(input: $input) {
      message
      reportCaseId
    }
  }
`;

export interface ReportMediaInput {
  maId: string;
  reasonCode: string;
  details?: string;
}

export interface ReportMediaData {
  reportMedia: {
    message: string;
    reportCaseId: string;
  };
}

export interface ReportMediaVariables {
  input: ReportMediaInput;
}



// --- GET REPORT DETAILS QUERY & TYPES ---

export const GET_REPORT_DETAILS = gql`
  query GetReportDetails($rcId: String!) {
    getReportDetails(rcId: $rcId) {
      rcId
      reportableType
      status
      reportsCount
      firstReportedAt
      lastReportedAt
      topReportedCategory
      reasonBreakdown {
        count
        reasonCode
        reasonLabel
      }
      media {
        maDescription
        maDuration
        maId
        maJwThumbNailUrl
        maS3ThumbnailKeyUrl
        maStatus
        maTitle
        maType
      }
      channel {
        chId
        chHandle
        followersCount
        followingCount
        chName
        chDescription
        chBannerUrl
        chAvatarUrl
        videosCount
      }
      action {
        modaId
        rcId
        actionType
        actionLabel
        message
        warningTypes {
          rrId
          rrCode
          rrLabel
        }
        durationDays
        suspensionPeriodLabel
        effectiveUntil
        takenBy {
          uId
          uEmailId
          uFullName
          uAvatarUrl
        }
        takenOn
      }
    }
  }
`;

export interface ReasonBreakdown {
  count: number;
  reasonCode: string;
  reasonLabel: string;
}

export interface MediaDetails {
  maDescription?: string | null;
  maDuration?: number | null;
  maId: string;
  maJwThumbNailUrl?: string | null;
  maS3ThumbnailKeyUrl?: string | null;
  maStatus: string;
  maTitle: string;
  maType: string;
}

export interface ChannelDetails {
  chId: string;
  chHandle: string;
  followersCount: number;
  followingCount: number;
  chName: string;
  chDescription?: string | null;
  chBannerUrl?: string | null;
  chAvatarUrl?: string | null;
  videosCount: number;
}

export interface WarningTypeDetail {
  rrId: string;
  rrCode: string;
  rrLabel: string;
}

export interface ReportDetails {
  rcId: string;
  reportableType: ReportableType | string;
  status: ReportCaseStatus | string;
  reportsCount: number;
  firstReportedAt: string;
  lastReportedAt: string;
  topReportedCategory?: string | null;
  reasonBreakdown: ReasonBreakdown[];
  media?: MediaDetails | null;
  channel?: ChannelDetails | null;
  action?: ModerationActionDetails | null;
}

export interface GetReportDetailsData {
  getReportDetails: ReportDetails;
}

export interface GetReportDetailsVariables {
  rcId: string;
}


// --- GET REPORT CASE REPORTERS QUERY & TYPES ---

export const GET_REPORT_CASE_REPORTERS = gql`
  query GetReportCaseReporters($input: GetReportCaseReportersInput!) {
    getReportCaseReporters(input: $input) {
      items {
        rId
        reporterInfo {
          uId
          uEmailId
          uFullName
          uAvatarUrl
        }
        reasonCode
        reasonLabel
        details
        createdAt
      }
      totalCount
    }
  }
`;

export interface ReporterInfo {
  uId: string;
  uEmailId: string;
  uFullName: string;
  uAvatarUrl?: string | null;
}

export interface ReportCaseReporterItem {
  rId: string;
  reporterInfo: ReporterInfo;
  reasonCode: string;
  reasonLabel: string;
  details?: string | null;
  createdAt: string;
}

export interface GetReportCaseReportersData {
  getReportCaseReporters: {
    items: ReportCaseReporterItem[];
    totalCount: number;
  };
}

export interface GetReportCaseReportersInput {
  rcId: string;
  limit: number;
  offset: number;
  search?: string;
}

export interface GetReportCaseReportersVariables {
  input: GetReportCaseReportersInput;
}


// --- TOGGLE REPORT CASE REVIEW STATUS MUTATION & TYPES ---

export const TOGGLE_REPORT_CASE_REVIEW_STATUS = gql`
  mutation ToggleReportCaseReviewStatus($rcId: String!) {
    toggleReportCaseReviewStatus(rcId: $rcId) {
      rcId
      status
    }
  }
`;

export interface ToggleReportCaseReviewStatusData {
  toggleReportCaseReviewStatus: {
    rcId: string;
    status: ReportCaseStatus | string;
  };
}

export interface ToggleReportCaseReviewStatusVariables {
  rcId: string;
}


export enum ModerationActionType {
  WARN_CREATOR = 'WARN_CREATOR',
  REMOVE_CONTENT = 'REMOVE_CONTENT',
  SUSPEND_CHANNEL = 'SUSPEND_CHANNEL',
  DELETE_CHANNEL = 'DELETE_CHANNEL',
}

// TAKE MODERATION ACTION MUTATION
export const TAKE_MODERATION_ACTION = gql`
  mutation TakeModerationAction($input: TakeModerationActionInput!) {
    takeModerationAction(input: $input) {
      message
      rcId
      modaId
      outcome
    }
  }
`;

export interface TakeModerationActionInput {
  rcId: string;
  actionType: ModerationActionType | string;
  notes: string;
  reasonIds?: string[];
  durationDays?: number | null;
}

export interface TakeModerationActionData {
  takeModerationAction: {
    message: string;
    rcId: string;
    modaId: string;
    outcome: string;
  };
}

export interface TakeModerationActionVariables {
  input: TakeModerationActionInput;
}